import { Activity, forwardRef } from "react";
import { Link } from "react-router-dom";
import type { SearchResults } from "../../../../apiClients/search/interfaces";
import { useLocalization, LocalizationKeys } from "../../../../i18n";
import styles from "./searchResultsDropdown.module.css";

interface SearchResultsDropdownProps {
  results: SearchResults | null;
  isSearching: boolean;
  error: Error | null;
  onSelectResult?: () => void;
}

export const SearchResultsDropdown = forwardRef<
  HTMLDivElement,
  SearchResultsDropdownProps
>(function SearchResultsDropdown(
  { results, isSearching, error, onSelectResult },
  ref,
) {
  const { t } = useLocalization();

  if (isSearching) {
    return (
      <div className={styles.dropdown} ref={ref}>
        <div className={styles.loading}>
          {t(LocalizationKeys.SEARCH_SEARCHING)}
        </div>
      </div>
    );
  }

  // Скрываем dropdown при ошибке
  if (error || !results) {
    return null;
  }

  const hasResults = Object.values(results).some((arr) => arr.length > 0);

  if (!hasResults) {
    return (
      <div className={styles.dropdown} ref={ref}>
        <div className={styles.empty}>
          {t(LocalizationKeys.SEARCH_NO_RESULTS)}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dropdown} ref={ref}>
      <Activity mode={results.movies.length > 0 ? "visible" : "hidden"}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t(LocalizationKeys.SEARCH_MOVIES)}
          </h3>
          <ul className={styles.resultList}>
            {results.movies.map((movie) => (
              <li key={movie.id}>
                <Link
                  to={`/content/${movie.id}`}
                  className={styles.resultItem}
                  onClick={onSelectResult}
                >
                  <Activity mode={movie.image ? "visible" : "hidden"}>
                    <img
                      src={movie.image}
                      alt={movie.title}
                      className={styles.resultImage}
                    />
                  </Activity>
                  <div className={styles.resultInfo}>
                    <span className={styles.resultTitle}>{movie.title}</span>
                    <span className={styles.resultMeta}>
                      {movie.releaseYear}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Activity>

      <Activity mode={results.serials.length > 0 ? "visible" : "hidden"}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t(LocalizationKeys.SEARCH_TV_SHOWS)}
          </h3>
          <ul className={styles.resultList}>
            {results.serials.map((serial) => (
              <li key={serial.id}>
                <Link
                  to={`/content/${serial.id}`}
                  className={styles.resultItem}
                  onClick={onSelectResult}
                >
                  <Activity mode={serial.image ? "visible" : "hidden"}>
                    <img
                      src={serial.image}
                      alt={serial.title}
                      className={styles.resultImage}
                    />
                  </Activity>
                  <div className={styles.resultInfo}>
                    <span className={styles.resultTitle}>{serial.title}</span>
                    <span className={styles.resultMeta}>
                      {serial.releaseYear}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Activity>

      <Activity mode={results.books.length > 0 ? "visible" : "hidden"}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t(LocalizationKeys.SEARCH_BOOKS)}
          </h3>
          <ul className={styles.resultList}>
            {results.books.map((book) => (
              <li key={book.id}>
                <Link
                  to={`/content/${book.id}`}
                  className={styles.resultItem}
                  onClick={onSelectResult}
                >
                  <Activity mode={book.image ? "visible" : "hidden"}>
                    <img
                      src={book.image}
                      alt={book.title}
                      className={styles.resultImage}
                    />
                  </Activity>
                  <div className={styles.resultInfo}>
                    <span className={styles.resultTitle}>{book.title}</span>
                    <span className={styles.resultMeta}>
                      {book.releaseYear}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Activity>

      <Activity
        mode={
          results.persons && results.persons.length > 0 ? "visible" : "hidden"
        }
      >
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t(LocalizationKeys.SEARCH_PERSONS)}
          </h3>
          <ul className={styles.resultList}>
            {results.persons?.map((person) => (
              <li key={person.id}>
                <Link
                  to={`/person/${person.id}`}
                  className={styles.resultItem}
                  onClick={onSelectResult}
                >
                  <Activity mode={person.image ? "visible" : "hidden"}>
                    <img
                      src={person.image}
                      alt={person.name}
                      className={styles.resultImage}
                    />
                  </Activity>
                  <div className={styles.resultInfo}>
                    <span className={styles.resultTitle}>{person.name}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Activity>

      <Activity
        mode={results.users && results.users.length > 0 ? "visible" : "hidden"}
      >
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t(LocalizationKeys.SEARCH_USERS)}
          </h3>
          <ul className={styles.resultList}>
            {results.users?.map((user) => (
              <li key={user.id}>
                <Link
                  to={`/user/${user.id}`}
                  className={styles.resultItem}
                  onClick={onSelectResult}
                >
                  <Activity mode={user.image ? "visible" : "hidden"}>
                    <img
                      src={user.image}
                      alt={user.username}
                      className={styles.resultImage}
                    />
                  </Activity>
                  <div className={styles.resultInfo}>
                    <span className={styles.resultTitle}>{user.username}</span>
                    <span className={styles.resultMeta}>
                      {t(LocalizationKeys.SEARCH_USER_REPUTATION)}:{" "}
                      {user.reputation}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Activity>

      <Activity
        mode={
          results.collections && results.collections.length > 0
            ? "visible"
            : "hidden"
        }
      >
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t(LocalizationKeys.SEARCH_COLLECTIONS)}
          </h3>
          <ul className={styles.resultList}>
            {results.collections?.map((collection) => (
              <li key={collection.id}>
                <Link
                  to={`/collection/${collection.id}`}
                  className={styles.resultItem}
                  onClick={onSelectResult}
                >
                  <Activity mode={collection.image ? "visible" : "hidden"}>
                    <img
                      src={collection.image}
                      alt={collection.name}
                      className={styles.resultImage}
                    />
                  </Activity>
                  <div className={styles.resultInfo}>
                    <span className={styles.resultTitle}>
                      {collection.name}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Activity>
    </div>
  );
});
